export const truffaTriangolazione = {
  id: 'static-truffa-triangolazione',
  slug: 'truffa-triangolazione',
  title: 'Attenzione alla "Triangolazione" nelle Compravendite di auto usate',
  excerpt: 'Cos\'è la triangolazione bancaria, come funziona e quali segnali d\'allarme osservare. Una guida pratica per riconoscere questa truffa e proteggere venditori e acquirenti.',
  body: `
    <div style="background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%); border-left: 4px solid #ef4444; padding: 1.5rem; border-radius: 12px; margin-bottom: 2rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
      <h3 style="color: #dc2626; margin-bottom: 1rem; font-size: 1.3rem;">
        <i class="fas fa-exclamation-triangle"></i> Attenzione alla truffa della triangolazione
      </h3>
      <p style="margin-bottom: 1rem;">La <strong>triangolazione bancaria</strong> è un raggiro sempre più diffuso nelle compravendite di auto usate. Il venditore riceve un bonifico che sembra regolare, ma in realtà proviene da un conto di terzi violato.</p>
      <p style="margin-bottom: 0;">Quando la banca blocca l'operazione fraudolenta, il venditore si ritrova senza auto e senza denaro.</p>
    </div>

    <div style="text-align: center; margin: 2rem 0;">
      <img src="/images/triangolazione.jpg" alt="Schema della truffa della triangolazione" style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);">
    </div>

    <h2>📋 Sommario in evidenza</h2>

    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1.5rem; margin: 2rem 0;">
      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
        <div style="display: flex; align-items: start; gap: 1rem;">
          <div style="background: #fef3c7; padding: 0.75rem; border-radius: 12px;">
            <i class="fas fa-exclamation-triangle" style="color: #f59e0b; font-size: 1.5rem;"></i>
          </div>
          <div>
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.1rem;">Il rischio</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">Il venditore consegna l'auto dopo un bonifico che proviene da un conto di terzi violato: il pagamento viene revocato e l'auto sparisce.</p>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
        <div style="display: flex; align-items: start; gap: 1rem;">
          <div style="background: #dbeafe; padding: 0.75rem; border-radius: 12px;">
            <i class="fas fa-money-bill-wave" style="color: #3b82f6; font-size: 1.5rem;"></i>
          </div>
          <div>
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.1rem;">Come funziona</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">Tre soggetti: venditore, truffatore e titolare ignaro del conto. Il bonifico "arriva", ma non dall'acquirente reale.</p>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
        <div style="display: flex; align-items: start; gap: 1rem;">
          <div style="background: #d1fae5; padding: 0.75rem; border-radius: 12px;">
            <i class="fas fa-shield-alt" style="color: #10b981; font-size: 1.5rem;"></i>
          </div>
          <div>
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.1rem;">Come difendersi</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">Identità e intestazione del conto devono coincidere. Usa piattaforme sicure e non consegnare l'auto finché tutto non è verificato.</p>
          </div>
        </div>
      </div>
    </div>

    <h2>🔍 Cos'è la "triangolazione" e perché è pericolosa</h2>

    <p>Con <strong>"triangolazione bancaria"</strong> si indica un raggiro in cui il denaro non proviene dal conto dell'acquirente, bensì da quello di un terzo soggetto, spesso compromesso tramite phishing o altre tecniche di frode informatica.</p>

    <p>Il venditore, vedendo l'accredito sul proprio conto, ritiene la transazione conclusa, consegna il veicolo e completa il passaggio di proprietà. Quando la vittima del furto denuncia l'operazione fraudolenta, si avvia un <strong>procedimento giudiziario</strong> che può durare mesi o anni. Nel frattempo, chi ha venduto rischia di ritrovarsi <strong>senza auto e senza incasso</strong>, con l'onere di dimostrare la propria buona fede in tribunale.</p>

    <h2>⚙️ Come si sviluppa tipicamente il raggiro</h2>

    <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 2.5rem; margin: 2rem 0; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
      <div style="margin-bottom: 2.5rem;">
        <div style="display: flex; align-items: start; gap: 2rem;">
          <div class="bg-gray-900 dark:bg-primary" style="color: white; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.25rem; flex-shrink: 0;">1</div>
          <div style="flex: 1;">
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.75rem; font-size: 1.15rem;">Contatto del finto acquirente</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0; line-height: 1.7;">Il truffatore risponde all'annuncio di vendita e si mostra collaborativo, interessato e disponibile.</p>
          </div>
        </div>
      </div>

      <div style="margin-bottom: 2.5rem;">
        <div style="display: flex; align-items: start; gap: 2rem;">
          <div class="bg-gray-900 dark:bg-primary" style="color: white; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.25rem; flex-shrink: 0;">2</div>
          <div style="flex: 1;">
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.75rem; font-size: 1.15rem;">Uso di un conto di terzi</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0; line-height: 1.7;">Tramite credenziali rubate (phishing, malware), il truffatore ordina un bonifico dal conto di un soggetto ignaro.</p>
          </div>
        </div>
      </div>

      <div style="margin-bottom: 2.5rem;">
        <div style="display: flex; align-items: start; gap: 2rem;">
          <div class="bg-gray-900 dark:bg-primary" style="color: white; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.25rem; flex-shrink: 0;">3</div>
          <div style="flex: 1;">
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.75rem; font-size: 1.15rem;">Accredito e consegna</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0; line-height: 1.7;">Il venditore vede il pagamento sul proprio conto, consegna l'auto e formalizza gli atti di passaggio di proprietà.</p>
          </div>
        </div>
      </div>

      <div style="margin-bottom: 2.5rem;">
        <div style="display: flex; align-items: start; gap: 2rem;">
          <div class="bg-gray-900 dark:bg-primary" style="color: white; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.25rem; flex-shrink: 0;">4</div>
          <div style="flex: 1;">
            <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.75rem; font-size: 1.15rem;">Denuncia e avvio del procedimento legale</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0; line-height: 1.7;">La vera vittima (titolare del conto violato) denuncia la frode. Si avvia un procedimento giudiziario per stabilire la restituzione dei fondi: sarà il giudice a decidere se e in quale misura il denaro debba tornare al truffato.</p>
          </div>
        </div>
      </div>

      <div style="margin-bottom: 0;">
        <div style="display: flex; align-items: start; gap: 2rem;">
          <div style="background: #ef4444; color: white; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.25rem; flex-shrink: 0;">5</div>
          <div style="flex: 1;">
            <h4 class="text-red-600 dark:text-red-400" style="margin-bottom: 0.75rem; font-size: 1.15rem;">Danno certo al venditore</h4>
            <p class="text-gray-600 dark:text-gray-300" style="margin: 0; line-height: 1.7;">Il veicolo può già essere stato rivenduto o esportato. <strong>In qualsiasi caso, il venditore subisce un danno</strong>: anche se il giudice dovesse riconoscergli parte dei fondi, il processo è lungo e costoso, e nel frattempo resta senza auto e senza incasso.</p>
          </div>
        </div>
      </div>
    </div>

    <h2>🛡️ Tutele pratiche: la checklist da seguire</h2>

    <div style="background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%); border-left: 4px solid #10b981; padding: 2rem; border-radius: 12px; margin: 2rem 0; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
      <h3 style="color: #059669; margin-bottom: 1.5rem;">
        <i class="fas fa-check-circle"></i> Regole d'oro per proteggersi
      </h3>
      
      <ul style="list-style: none; padding: 0; margin: 0;">
        <li style="margin-bottom: 1rem; display: flex; align-items: start; gap: 0.75rem;">
          <i class="fas fa-check-circle" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
          <span><strong>Allinea identità e denaro:</strong> il pagamento deve arrivare da un conto intestato <em>alla stessa persona</em> che firma l'acquisto.</span>
        </li>
        <li style="margin-bottom: 1rem; display: flex; align-items: start; gap: 0.75rem;">
          <i class="fas fa-check-circle" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
          <span><strong>Evita conti di terzi o esteri:</strong> rifiuta bonifici provenienti da nominativi che non coincidono con l'acquirente.</span>
        </li>
        <li style="margin-bottom: 1rem; display: flex; align-items: start; gap: 0.75rem;">
          <i class="fas fa-check-circle" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
          <span><strong>Non avere fretta:</strong> non consegnare il veicolo finché il <em>passaggio di proprietà</em> non è perfezionato e verificato.</span>
        </li>
        <li style="margin-bottom: 1rem; display: flex; align-items: start; gap: 0.75rem;">
          <i class="fas fa-check-circle" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
          <span><strong>Preferisci piattaforme sicure:</strong> servizi escrow/terzi garantiscono verifiche su identità, fondi e documenti.</span>
        </li>
        <li style="margin-bottom: 0; display: flex; align-items: start; gap: 0.75rem;">
          <i class="fas fa-check-circle" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
          <span><strong>Documenta tutto:</strong> conserva scambi, contratti, ricevute e screenshot dell'accredito.</span>
        </li>
      </ul>
    </div>

    <h2>✅ Verifiche essenziali</h2>

    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; margin: 2rem 0;">
      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
        <h3 style="color: #00bf8f; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem;">
          <i class="fas fa-user-check"></i> Verifiche sull'acquirente
        </h3>
        <ul style="list-style: none; padding: 0; margin: 0;">
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Documento e codice fiscale coerenti</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Conto bancario intestato all'acquirente</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Recapiti verificabili e stabili</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Disponibilità a usare servizi terzi di garanzia</span>
          </li>
        </ul>
      </div>

      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
        <h3 style="color: #00bf8f; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem;">
          <i class="fas fa-file-alt"></i> Documenti e passaggi
        </h3>
        <ul style="list-style: none; padding: 0; margin: 0;">
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Atto di vendita compilato correttamente</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Verifica PRA/ACI ove necessario</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0.75rem; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Ricevute e quietanze conservate</span>
          </li>
          <li class="text-gray-700 dark:text-gray-300" style="margin-bottom: 0; display: flex; align-items: start; gap: 0.5rem;">
            <i class="fas fa-check" style="color: #10b981; margin-top: 0.25rem; flex-shrink: 0;"></i>
            <span>Consegna chiavi solo a formalità completate</span>
          </li>
        </ul>
      </div>
    </div>

    <div style="background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%); border-left: 4px solid #f59e0b; padding: 1.5rem; border-radius: 12px; margin: 2rem 0; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
      <div style="display: flex; align-items: start; gap: 1rem;">
        <i class="fas fa-exclamation-triangle" style="color: #f59e0b; font-size: 1.5rem; flex-shrink: 0;"></i>
        <div>
          <h4 style="color: #92400e; margin-bottom: 0.5rem; font-size: 1.1rem;">Attenzione ai bonifici da conti intestati a terzi</h4>
          <p style="color: #92400e; margin: 0;">Anche con accredito visibile, il pagamento può essere oggetto di contestazione se parte da un conto non intestato all'acquirente. <strong>Non è un blocco automatico</strong>: si avvia un procedimento giudiziario dove il giudice deciderà se e in quale misura restituire i fondi al truffato. In ogni caso, il venditore subisce un danno. Mantieni il veicolo finché identità, fondi e passaggi amministrativi non sono verificati e coerenti.</p>
        </div>
      </div>
    </div>

    <h2>📖 Esempio concreto</h2>

    <div class="bg-gray-50 dark:bg-gray-800/50" style="border-radius: 12px; padding: 2rem; margin: 2rem 0; border: 1px solid;" class="border-gray-200 dark:border-gray-700">
      <p class="text-gray-600 dark:text-gray-300" style="font-style: italic; line-height: 1.8;">
        <strong>Luca</strong> mette in vendita la sua auto su un portale di annunci. <strong>Marco</strong>, il finto acquirente, si mostra molto interessato ed effettua un bonifico che in realtà parte dal conto di <strong>Luigi</strong>, violato tramite phishing.
      </p>
      <p class="text-gray-600 dark:text-gray-300" style="font-style: italic; line-height: 1.8;">
        Luca vede l'accredito sul suo conto e, convinto che tutto sia regolare, consegna il veicolo e completa il passaggio di proprietà.
      </p>
      <p class="text-gray-600 dark:text-gray-300" style="font-style: italic; line-height: 1.8; margin-bottom: 0;">
        Poche ore dopo, Luigi si accorge del furto e denuncia l'operazione: la banca blocca il bonifico e Luca resta senza incasso, mentre l'auto potrebbe essere già passata di mano o esportata all'estero.
      </p>
    </div>

    <h2>🔒 Preferisci canali sicuri</h2>

    <p>Piattaforme specializzate come <strong>Owny</strong> – con cui UNASCA collabora – offrono <strong>verifica identità</strong>, <strong>controllo della provenienza dei fondi</strong>, <strong>gestione documentale</strong> e <strong>rilascio del pagamento solo a condizioni rispettate</strong>. Questi strumenti sono stati ideati proprio per contrastare truffe come la triangolazione e proteggere sia venditori che acquirenti.</p>

    <p>Se la controparte rifiuta procedure trasparenti o accelera in modo anomalo la transazione, è un <strong>campanello d'allarme</strong>: meglio interrompere e affidarsi a piattaforme sicure che garantiscono la tutela di entrambe le parti.</p>

    <h2>❓ Domande frequenti</h2>

    <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 2rem; margin: 2rem 0; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);">
      <div style="margin-bottom: 2rem;">
        <h4 style="color: #00bf8f; margin-bottom: 0.75rem;">
          <i class="fas fa-question-circle"></i> Se vedo l'accredito posso consegnare l'auto?
        </h4>
        <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">Meglio attendere che il passaggio di proprietà sia concluso e che la banca confermi l'assenza di anomalie (nome ordinante conforme all'acquirente). I bonifici possono essere contestati o revocati in caso di frode.</p>
      </div>

      <div style="margin-bottom: 2rem;">
        <h4 style="color: #00bf8f; margin-bottom: 0.75rem;">
          <i class="fas fa-question-circle"></i> Il bonifico istantaneo è più sicuro?
        </h4>
        <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">È più rapido, non necessariamente più sicuro. Se parte da un conto di terzi compromesso, rimane un pagamento illecito: i rischi legali ed economici per il venditore permangono.</p>
      </div>

      <div style="margin-bottom: 0;">
        <h4 style="color: #00bf8f; margin-bottom: 0.75rem;">
          <i class="fas fa-question-circle"></i> Quali segnali d'allarme devo riconoscere?
        </h4>
        <p class="text-gray-600 dark:text-gray-300" style="margin: 0;">Intestatari non allineati tra bonifico e acquirente; richiesta di usare conti o intermediari "amici"; urgenza immotivata; reticenza a condividere documenti o a usare piattaforme terze.</p>
      </div>
    </div>

    <h2>🤝 UNASCA è convenzionata con Owny</h2>

    <div style="background: linear-gradient(135deg, rgba(0, 191, 143, 0.15) 0%, rgba(0, 191, 143, 0.05) 100%); border-left: 4px solid #00bf8f; border-radius: 12px; padding: 2.5rem; margin: 2rem 0; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);">
      <div style="text-align: center; margin-bottom: 1.5rem;">
        <img src="/images/owny.png" alt="Logo Owny" style="max-width: 220px; height: auto;">
      </div>

      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 2rem; margin-bottom: 1.5rem;">
        <h3 style="color: #00bf8f; margin-bottom: 1rem; text-align: center; font-size: 1.3rem;">
          <i class="fas fa-shield-alt"></i> La soluzione contro le truffe nella compravendita
        </h3>
        <p class="text-gray-800 dark:text-gray-200" style="font-size: 1.1rem; line-height: 1.8; margin-bottom: 1rem; text-align: center;">
          <strong>UNASCA ha stipulato una collaborazione con Owny</strong>, piattaforma innovativa ideata specificamente per <strong>contrastare le truffe come la triangolazione</strong> e risolvere i problemi legati al pagamento dei veicoli.
        </p>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin-bottom: 1.5rem;">
        <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; text-align: center;">
          <div style="background: #d1fae5; width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <i class="fas fa-user-shield" style="color: #00bf8f; font-size: 1.8rem;"></i>
          </div>
          <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.05rem;">Verifica identità</h4>
          <p class="text-gray-600 dark:text-gray-300" style="margin: 0; font-size: 0.95rem; line-height: 1.6;">Controllo completo dell'identità di acquirente e venditore</p>
        </div>

        <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; text-align: center;">
          <div style="background: #d1fae5; width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <i class="fas fa-money-check-alt" style="color: #00bf8f; font-size: 1.8rem;"></i>
          </div>
          <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.05rem;">Garanzia sui fondi</h4>
          <p class="text-gray-600 dark:text-gray-300" style="margin: 0; font-size: 0.95rem; line-height: 1.6;">Verifica della provenienza dei fondi e protezione del pagamento</p>
        </div>

        <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; text-align: center;">
          <div style="background: #d1fae5; width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem;">
            <i class="fas fa-file-contract" style="color: #00bf8f; font-size: 1.8rem;"></i>
          </div>
          <h4 class="text-gray-900 dark:text-white" style="margin-bottom: 0.5rem; font-size: 1.05rem;">Gestione documentale</h4>
          <p class="text-gray-600 dark:text-gray-300" style="margin: 0; font-size: 0.95rem; line-height: 1.6;">Tutti i documenti verificati e archiviati in sicurezza</p>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800" style="border-radius: 12px; padding: 1.5rem; text-align: center;">
        <p style="color: #00bf8f; font-weight: 700; font-size: 1.15rem; margin: 0;">
          <i class="fas fa-check-circle"></i> Grazie alla convenzione UNASCA, i nostri associati possono operare in totale sicurezza
        </p>
      </div>
    </div>

    <div class="border-gray-200 dark:border-gray-700" style="margin-top: 3rem; padding-top: 2rem; border-top: 2px solid; text-align: center;">
      <p class="text-gray-600 dark:text-gray-400" style="font-style: italic; font-size: 1.1rem;">
        Proteggi la tua compravendita: adotta procedure chiare e strumenti di verifica prima di consegnare l'auto.
      </p>
    </div>
  `,
  featuredImage: '/images/triangolazione.jpg',
  category: {
    id: 'sicurezza',
    name: 'Sicurezza',
    slug: 'sicurezza',
    color: '#EF4444'
  },
  tags: ['Truffe', 'Sicurezza', 'Compravendita Auto', 'Triangolazione', 'Tutela Consumatori'],
  author: {
    id: 'unasca-redazione',
    username: 'UNASCA',
    profile: {
      firstName: 'Redazione',
      lastName: 'UNASCA',
      fullName: 'Redazione UNASCA'
    }
  },
  status: 'PUBLISHED' as const,
  visibility: 'PUBLIC' as const,
  publishedAt: '2025-10-29T10:00:00Z',
  createdAt: '2025-10-29T10:00:00Z',
  updatedAt: '2025-10-29T10:00:00Z',
  metadata: {
    views: 0,
    likeCount: 0,
    shares: 0
  },
  readingTime: 6
};

